import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { NavLink } from "react-router";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { Button } from "@/shared/components/ui/button";
import { Search } from "lucide-react";
import { useQpmOfferListQuery } from "@/services/backend/qpm/offers/api/offer-api";
import { Offer } from "@/services/backend/qpm/offers/types/offer";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { Badge } from "@/shared/components/ui/badge";
import { offerTypeLabel } from "@/routes/processes/[processId]/qpm/offers/_components/offer-document-profile-form";

export type OfferTableComponentProps = {
  processId: string;
};

export function OfferTableComponent({ processId }: OfferTableComponentProps) {
  const {
    data: list,
    isLoading,
    error,
  } = useQpmOfferListQuery({
    process: {
      active: true,
      values: [processId],
    },
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">{t("Aktuelles")}</TableHead>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Angebotsart")}</TableHead>
            <TableHead>{t("Erstellt am")}</TableHead>
            <TableHead className="w-full">{t("Heruntergeladen")}</TableHead>
            <TableHead className="justify-end">{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead>{t("Angebotsart")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead>{t("Angebotsdatum")}</TableHead>
          <TableHead>{t("Heruntergeladen")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...(list?.items ?? [])].sort(byDate).map((offer) => {
          const offerType = offer.documentProfile?.offerType?.value;

          return (
            <TableRow key={offer.id}>
              <TableCell>
                <NavLink to={`${offer.id}`}>
                  <DocumentNumberById documentId={offer.id} />
                </NavLink>
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <Badge variant="secondary">
                    {offerType ? offerTypeLabel(offerType) : t("Unbekannt")}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                {formatDate(offer.createdAt)}, {formatTime(offer.createdAt)}
              </TableCell>
              <TableCell>
                {offer.createdAt && formatDate(offer.createdAt)}
              </TableCell>
              <TableCell>{offer.immutable ? "ja" : "nein"}</TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <NavLink to={`${offer.id}`}>
                    <Button variant="ghost">
                      <Search className="h-4 w-4" />
                    </Button>
                  </NavLink>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
        {list?.items?.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} className="text-center">
              {t("Keine Angebote gefunden")}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function byDate(a: Offer, b: Offer) {
  switch (true) {
    case a === null && b === null:
      return 0;
    case a === null && b !== null:
      return 1;
    case a !== null && b === null:
      return -1;
    default:
      return b.createdAt!.localeCompare(a.createdAt!);
  }
}
